import { posthog, type PostHogConfig } from 'posthog-js';
import {
  PostHogErrorBoundary,
  PostHogProvider as PostHogProviderBase,
} from 'posthog-js/react';

import { config } from '@/config/config';
import { detectInAppBrowser } from '@/lib/posthog/inAppBrowser';
import {
  handoffDistinctIdParam,
  readHandoffDistinctId,
  readPersistedUserState,
} from '@/modules/auth/inAppHandoff';
import { isDevEnv, isLocalEnv, isProdEnv } from '@/utils/env';

const isPostHogTrackingEnabled = isDevEnv || isProdEnv;

const posthogConfig: Partial<PostHogConfig> = {
  name: 'finlay-frontend',
  api_host: config.external.posthogHost,
  ui_host: 'https://eu.posthog.com',
  defaults: '2025-05-24',
  autocapture: isPostHogTrackingEnabled,
  capture_exceptions: isPostHogTrackingEnabled,
  capture_pageview: isPostHogTrackingEnabled,
  debug: isLocalEnv,

  session_recording: {
    maskAllInputs: false,
    maskInputOptions: {
      password: true,
      email: true,
    },
  },
};

// posthog-js saves its state under this name in localStorage and a cookie
// (its default `persistence`, which `posthogConfig` doesn't change).
const persistenceName = `ph_${config.external.posthogKey
  .replace(/\+/g, 'PL')
  .replace(/\//g, 'SL')
  .replace(/=/g, 'EQ')}_posthog`;

const readSavedPostHogState = () => {
  let local: string | null = null;
  try {
    local = window.localStorage.getItem(persistenceName);
  } catch {
    // Storage can be blocked; the cookie may still say who this is.
  }
  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${persistenceName}=`))
    ?.slice(persistenceName.length + 1);
  return readPersistedUserState(local, cookie);
};

// A sign-up opened from the visitor's other browser or device arrives with
// their PostHog id, so both count as one person: the in-app browser hand-off
// (`buildHandoffUrl`) and a phone's "send yourself the link"
// (`buildContinueUrl`). The param is removed from the address bar whether or
// not it is used. The router doesn't see this strip, so `SignUp` drops the
// param from the URLs it builds as well (`getSignUpParams`).
const handoffUrl = new URL(window.location.href);
if (handoffUrl.searchParams.has(handoffDistinctIdParam)) {
  const handoffDistinctId = readHandoffDistinctId({
    href: handoffUrl.href,
    persistedUserState: readSavedPostHogState(),
  });
  if (handoffDistinctId) {
    posthogConfig.bootstrap = { distinctID: handoffDistinctId };
  }
  handoffUrl.searchParams.delete(handoffDistinctIdParam);
  window.history.replaceState(window.history.state, '', handoffUrl);
}

posthog.init(config.external.posthogKey, posthogConfig);
// Sent with every event so funnels can split visitors who arrived inside an
// app's built-in browser (e.g. tapping a LinkedIn ad) from regular browsers.
posthog.register({ inAppBrowser: detectInAppBrowser(navigator.userAgent) });

export const PostHogProvider = ({
  children,
  errorFallback,
}: {
  children: React.ReactNode;
  errorFallback: React.ReactNode;
}) => {
  return (
    <PostHogProviderBase client={posthog}>
      <PostHogErrorBoundary fallback={errorFallback}>
        {children}
      </PostHogErrorBoundary>
    </PostHogProviderBase>
  );
};
